/**
 * Navbar Component
 * Top navigation bar with branding, search, and user menu
 * L0-CMD-2026-0120-011 - Enhanced with company context display
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';
import { projectApi, clientApi } from '../services/api';

interface Company {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  role: string;
}

interface SearchResult {
  type: 'project' | 'client';
  id: number;
  title: string;
  subtitle: string;
}

interface NavbarProps {
  onMenuClick: () => void;
  onSettingsClick?: () => void;
  onTeamClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick, onSettingsClick, onTeamClick }) => {
  const { user, tenants, currentTenant, switchTenant, logout, role } = useAuth();
  const { branding } = useTenant();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTenantMenu, setShowTenantMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchCache = useRef<{ projects: any[]; clients: any[] } | null>(null);
  const latestQuery = useRef('');

  // Fetch company context on mount
  useEffect(() => {
    fetchCompanyContext();
  }, [currentTenant]);

  const fetchCompanyContext = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch('/api/companies/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
        const current = data.companies?.find((c: Company) => c.id === data.current_company_id);
        setCurrentCompany(current || data.companies?.[0] || null);
      }
    } catch (err) {
      console.error('Failed to fetch company context:', err);
    }
  };

  const handleSwitchCompany = async (companyId: number) => {
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`/api/companies/switch/${companyId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Also use the existing switchTenant if available
      if (switchTenant) {
        await switchTenant(companyId);
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to switch company:', err);
    }
    setShowTenantMenu(false);
  };

  const isAdmin = role === 'owner' || role === 'admin';

  // Mock notifications
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'New project assigned', message: 'PRJ-2024-0042 has been assigned to you', time: '5m ago', unread: true },
    { id: 2, title: 'Invoice paid', message: 'Invoice #INV-2024-0089 was paid', time: '1h ago', unread: true },
    { id: 3, title: 'Daily check-in reminder', message: 'Don\'t forget to check in!', time: '2h ago', unread: false },
  ]);

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleMarkAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  };

  // Cmd/Ctrl+K focuses the search input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search projects and clients (fetched once, filtered locally)
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    const q = query.trim().toLowerCase();
    latestQuery.current = q;

    if (!q) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }

    if (!searchCache.current) {
      const [projRes, clientRes] = await Promise.all([projectApi.list(), clientApi.list()]);
      searchCache.current = {
        projects: projRes.success ? (projRes.data?.projects || []) : [],
        clients: clientRes.success ? (clientRes.data?.clients || []) : [],
      };
      // A newer keystroke ran while we were fetching; let it drive the results
      if (latestQuery.current !== q) return;
    }

    const projectMatches: SearchResult[] = searchCache.current.projects
      .filter((p: any) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.project_number || '').toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map((p: any) => ({
        type: 'project' as const,
        id: p.id,
        title: p.name,
        subtitle: `${p.project_number} · ${p.status}`,
      }));

    const clientMatches: SearchResult[] = searchCache.current.clients
      .filter((c: any) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.contact_name || '').toLowerCase().includes(q)
      )
      .slice(0, 3)
      .map((c: any) => ({
        type: 'client' as const,
        id: c.id,
        title: c.name,
        subtitle: c.contact_name || 'Client',
      }));

    setSearchResults([...projectMatches, ...clientMatches]);
    setSearchOpen(true);
  };

  const handleSearchSelect = (result: SearchResult) => {
    setSearchOpen(false);
    setSearchQuery('');
    if (role === 'client') {
      navigate('/client/projects');
    } else if (role === 'drafter') {
      navigate('/drafter/assignments');
    } else {
      navigate('/admin/projects');
    }
  };

  return (
    <nav className="navbar">
      {/* Left Section - Menu & Logo */}
      <div className="navbar-left">
        <button className="menu-toggle" onClick={onMenuClick}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
        
        <div className="logo">
          <img 
            src={branding.logo_url} 
            alt={branding.company_name}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <span className="logo-text">{branding.company_name}</span>
        </div>
      </div>

      {/* Center Section - Search */}
      <div className="navbar-center">
        <div className="search-container">
          <div className="search-bar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search projects, clients, drawings..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => { if (searchQuery.trim() && searchResults.length > 0) setSearchOpen(true); }}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            />
            <span className="search-shortcut">⌘K</span>
          </div>

          {searchOpen && (
            <div className="search-results">
              {searchResults.length === 0 ? (
                <div className="search-empty">No matching projects or clients</div>
              ) : (
                searchResults.map(result => (
                  <button
                    key={`${result.type}-${result.id}`}
                    className="search-result-item"
                    onMouseDown={() => handleSearchSelect(result)}
                  >
                    <span className="result-type">{result.type}</span>
                    <span className="result-text">
                      <span className="result-title">{result.title}</span>
                      <span className="result-subtitle">{result.subtitle}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Actions & User */}
      <div className="navbar-right">
        {/* Company Context Display */}
        <div className="company-context">
          <button
            className="company-btn"
            onClick={() => setShowTenantMenu(!showTenantMenu)}
          >
            <div className="company-logo">
              {currentCompany?.logo_url ? (
                <img
                  src={currentCompany.logo_url}
                  alt={currentCompany.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <span
                className={`logo-fallback ${currentCompany?.logo_url ? 'hidden' : ''}`}
                style={{ backgroundColor: currentCompany?.primary_color || branding.primary_color }}
              >
                {(currentCompany?.name || currentTenant?.name || 'C').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="company-info">
              <span className="company-name">{currentCompany?.name || currentTenant?.name}</span>
              <span className="company-role">{currentCompany?.role || role}</span>
            </div>
            {companies.length > 1 && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`dropdown-chevron ${showTenantMenu ? 'open' : ''}`}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            )}
          </button>

          {showTenantMenu && (
            <div className="dropdown-menu company-menu">
              {companies.length > 1 && (
                <>
                  <div className="dropdown-header">Switch Company</div>
                  {companies.map(company => (
                    <button
                      key={company.id}
                      className={`dropdown-item ${company.id === currentCompany?.id ? 'active' : ''}`}
                      onClick={() => handleSwitchCompany(company.id)}
                    >
                      <div className="item-logo">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt={company.name} />
                        ) : (
                          <span className="logo-fallback small" style={{ backgroundColor: company.primary_color }}>
                            {company.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <span className="item-text">
                        <span className="item-name">{company.name}</span>
                        <span className="item-role">{company.role}</span>
                      </span>
                      {company.id === currentCompany?.id && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </button>
                  ))}
                  <div className="dropdown-divider"></div>
                </>
              )}

              {/* Admin actions */}
              {isAdmin && (
                <>
                  <button className="dropdown-item" onClick={() => { onTeamClick?.(); setShowTenantMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span>Manage Team</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { onSettingsClick?.(); setShowTenantMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    <span>Company Settings</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="notifications">
          <button 
            className="icon-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>
          
          {showNotifications && (
            <div className="dropdown-menu notifications-menu">
              <div className="dropdown-header">
                Notifications
                <button className="mark-read" onClick={handleMarkAllRead}>Mark all read</button>
              </div>
              <div className="notifications-list">
                {notifications.map(notif => (
                  <div key={notif.id} className={`notification-item ${notif.unread ? 'unread' : ''}`}>
                    <div className="notif-content">
                      <span className="notif-title">{notif.title}</span>
                      <span className="notif-message">{notif.message}</span>
                      <span className="notif-time">{notif.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="dropdown-footer">
                <a href="/notifications">View all notifications</a>
              </div>
            </div>
          )}
        </div>

        {/* Help */}
        <div className="help-menu">
          <button className="icon-btn" onClick={() => setShowHelp(!showHelp)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
          </button>

          {showHelp && (
            <div className="dropdown-menu help-dropdown">
              <div className="dropdown-header">Help</div>
              <div className="help-content">
                <div className="help-row">
                  <span>Focus search</span>
                  <span className="help-key">Ctrl / ⌘ + K</span>
                </div>
                <p className="help-note">
                  Use the search bar to find projects and clients by name or number.
                </p>
                <p className="help-note">
                  For account or billing questions, contact your company administrator.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="user-menu">
          <button 
            className="user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          </button>
          
          {showUserMenu && (
            <div className="dropdown-menu user-dropdown">
              <div className="user-info">
                <div className="user-avatar large">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-details">
                  <span className="user-name">{user?.name}</span>
                  <span className="user-email">{user?.email}</span>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <a href="/settings/profile" className="dropdown-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Profile Settings
              </a>
              <a href="/settings/preferences" className="dropdown-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Preferences
              </a>
              {user?.is_platform_admin && (
                <a href="/platform/admin" className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Platform Admin
                </a>
              )}
              <div className="dropdown-divider"></div>
              <button className="dropdown-item danger" onClick={logout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 64px;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color, #334155);
          display: flex;
          align-items: center;
          padding: 0 16px;
          z-index: 1000;
        }

        .navbar-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .menu-toggle {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
        }

        .menu-toggle:hover {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .logo img {
          height: 32px;
        }

        .logo-text {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .navbar-center {
          flex: 1;
          display: flex;
          justify-content: center;
          padding: 0 40px;
        }

        .search-container {
          position: relative;
          width: 100%;
          max-width: 480px;
        }

        .search-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--bg-tertiary, #334155);
          border-radius: 8px;
          padding: 8px 16px;
          width: 100%;
          max-width: 480px;
        }

        .search-results {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          z-index: 1001;
        }

        .search-result-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 10px 16px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
        }

        .search-result-item:hover {
          background: var(--bg-tertiary, #334155);
        }

        .result-type {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--text-secondary);
          background: var(--bg-tertiary, #334155);
          padding: 2px 6px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .result-text {
          display: flex;
          flex-direction: column;
        }

        .result-title {
          font-size: 14px;
          color: var(--text-primary);
        }

        .result-subtitle {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .search-empty {
          padding: 12px 16px;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .search-bar svg {
          color: var(--text-secondary);
        }

        .search-bar input {
          flex: 1;
          background: none;
          border: none;
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
        }

        .search-bar input::placeholder {
          color: var(--text-secondary);
        }

        .search-shortcut {
          font-size: 12px;
          color: var(--text-secondary);
          background: var(--bg-secondary);
          padding: 2px 6px;
          border-radius: 4px;
        }

        .navbar-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .icon-btn {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          position: relative;
        }

        .icon-btn:hover {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .notification-badge {
          position: absolute;
          top: 2px;
          right: 2px;
          background: var(--danger, #ef4444);
          color: white;
          font-size: 10px;
          font-weight: 600;
          min-width: 16px;
          height: 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .company-context {
          position: relative;
        }

        .company-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--bg-tertiary, #334155);
          border: none;
          padding: 6px 12px;
          border-radius: 8px;
          cursor: pointer;
          color: var(--text-primary);
          transition: background 0.2s;
        }

        .company-btn:hover {
          background: var(--bg-secondary);
        }

        .company-logo {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .company-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .logo-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 14px;
          border-radius: 6px;
        }

        .logo-fallback.hidden {
          display: none;
        }

        .logo-fallback.small {
          width: 28px;
          height: 28px;
          font-size: 12px;
        }

        .company-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .company-name {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .company-role {
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        .dropdown-chevron {
          color: var(--text-secondary);
          transition: transform 0.2s;
        }

        .dropdown-chevron.open {
          transform: rotate(180deg);
        }

        .company-menu {
          width: 280px;
        }

        .company-menu .item-logo {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          overflow: hidden;
          flex-shrink: 0;
        }

        .company-menu .item-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .tenant-switcher {
          position: relative;
        }

        .tenant-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-tertiary, #334155);
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          color: var(--text-primary);
        }

        .tenant-avatar {
          width: 24px;
          height: 24px;
          background: var(--primary);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          color: white;
        }

        .tenant-name {
          font-size: 14px;
        }

        .help-menu {
          position: relative;
        }

        .help-dropdown {
          width: 280px;
        }

        .help-content {
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .help-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          color: var(--text-primary);
        }

        .help-key {
          font-size: 12px;
          color: var(--text-secondary);
          background: var(--bg-tertiary, #334155);
          padding: 2px 6px;
          border-radius: 4px;
        }

        .help-note {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0;
        }

        .user-menu {
          position: relative;
        }

        .user-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          background: var(--primary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: white;
        }

        .user-avatar.large {
          width: 48px;
          height: 48px;
          font-size: 18px;
        }

        /* Dropdown Menus */
        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          min-width: 240px;
          overflow: hidden;
          z-index: 1001;
        }

        .dropdown-header {
          padding: 12px 16px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          border-bottom: 1px solid var(--border-color, #334155);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dropdown-header .mark-read {
          background: none;
          border: none;
          color: var(--primary);
          font-size: 12px;
          cursor: pointer;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          color: var(--text-primary);
          text-decoration: none;
          cursor: pointer;
          transition: background 0.2s;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-size: 14px;
        }

        .dropdown-item:hover {
          background: var(--bg-tertiary, #334155);
        }

        .dropdown-item.active {
          background: rgba(37, 99, 235, 0.1);
        }

        .dropdown-item.danger {
          color: var(--danger, #ef4444);
        }

        .dropdown-divider {
          height: 1px;
          background: var(--border-color, #334155);
          margin: 4px 0;
        }

        .dropdown-footer {
          padding: 12px 16px;
          border-top: 1px solid var(--border-color, #334155);
          text-align: center;
        }

        .dropdown-footer a {
          color: var(--primary);
          text-decoration: none;
          font-size: 14px;
        }

        /* User Dropdown */
        .user-dropdown {
          width: 280px;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
        }

        .user-details {
          display: flex;
          flex-direction: column;
        }

        .user-name {
          font-weight: 500;
          color: var(--text-primary);
        }

        .user-email {
          font-size: 13px;
          color: var(--text-secondary);
        }

        /* Notifications Menu */
        .notifications-menu {
          width: 360px;
        }

        .notifications-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .notification-item {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color, #334155);
        }

        .notification-item.unread {
          background: rgba(37, 99, 235, 0.05);
        }

        .notification-item:hover {
          background: var(--bg-tertiary, #334155);
        }

        .notif-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .notif-title {
          font-weight: 500;
          color: var(--text-primary);
          font-size: 14px;
        }

        .notif-message {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .notif-time {
          font-size: 12px;
          color: var(--text-muted, #64748b);
        }

        /* Tenant Menu */
        .tenant-menu .item-avatar {
          width: 32px;
          height: 32px;
          background: var(--primary);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          color: white;
        }

        .tenant-menu .item-text {
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .tenant-menu .item-name {
          font-weight: 500;
        }

        .tenant-menu .item-role {
          font-size: 12px;
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        @media (max-width: 768px) {
          .navbar-center {
            display: none;
          }

          .tenant-name {
            display: none;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
